import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import copy from "../../public/locales/en/textQuality.json";
import { useAppDispatch, useAppSelector } from "../store";
import {
  loadQualityText,
  saveQualityText,
} from "../store/slices/textQualitySlice";

import type { TextQualityReport } from "@lingoshade/domain";
import type { ReactElement, ReactNode } from "react";

function highlightedOriginal(report: TextQualityReport): ReactNode[] {
  const nodes: ReactNode[] = [];
  let end = 0;
  for (const issue of report.issues) {
    if (issue.start < end) continue;
    nodes.push(report.content.slice(end, issue.start));
    nodes.push(
      <Box
        component="mark"
        key={issue.start}
        sx={{ bgcolor: "warning.light", color: "text.primary" }}
      >
        {report.content.slice(issue.start, issue.end)}
      </Box>,
    );
    end = issue.end;
  }
  nodes.push(report.content.slice(end));
  return nodes;
}

export default function TextQualityEditor({
  report,
}: {
  report: TextQualityReport;
}): ReactElement {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { saving, saved, error } = useAppSelector((state) => state.textQuality);
  const [draft, setDraft] = useState(report.content);
  const dirty = draft !== report.content;
  useEffect((): (() => void) => {
    const preventUnload = (event: BeforeUnloadEvent): void => {
      if (dirty) event.preventDefault();
    };
    window.addEventListener("beforeunload", preventUnload);
    return (): void =>
      window.removeEventListener("beforeunload", preventUnload);
  }, [dirty]);
  const canDiscard = (): boolean =>
    !dirty || window.confirm(copy.discardConfirm);
  return (
    <Stack spacing={2}>
      <Box>
        <Button
          disabled={saving}
          onClick={(): void => {
            if (canDiscard()) navigate("/text-quality");
          }}
        >
          {copy.back}
        </Button>
      </Box>
      <Typography variant="h4" sx={{ overflowWrap: "anywhere" }}>
        {report.topic || copy.untitled}
      </Typography>
      <Stack direction="row" spacing={1}>
        <Chip label={`${report.language} · ${report.level}`} />
        <Chip label={report.authorId ? copy.user : copy.system} />
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
      {saved && <Alert severity="success">{copy.saved}</Alert>}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6">{copy.issues}</Typography>
        {!report.issues.length && <Typography>{copy.clean}</Typography>}
        {report.issues.map((issue) => (
          <Typography
            key={`${issue.ruleId}-${issue.start}`}
            sx={{ overflowWrap: "anywhere" }}
          >
            {copy.rules[issue.ruleId].name} · {copy.line} {issue.line}:{" "}
            <code>{issue.fragment}</code>
          </Typography>
        ))}
        {report.issues.length > 0 && (
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            {copy.rules["speaker-labels"].description}
          </Typography>
        )}
      </Paper>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 2, height: "100%" }}>
            <Typography variant="h6">{copy.original}</Typography>
            <Typography
              sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", mt: 1 }}
            >
              {highlightedOriginal(report)}
            </Typography>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2}>
            <TextField
              label={copy.draft}
              multiline
              minRows={12}
              fullWidth
              value={draft}
              disabled={saving}
              onChange={(event): void => setDraft(event.target.value)}
            />
            <Typography variant="body2" color="text.secondary">
              {copy.saveHint}
            </Typography>
            <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
              <Button
                variant="outlined"
                disabled={saving || report.suggestedContent === report.content}
                onClick={(): void => {
                  if (canDiscard()) setDraft(report.suggestedContent);
                }}
              >
                {copy.suggest}
              </Button>
              <Button
                disabled={saving || !dirty}
                onClick={(): void => {
                  if (canDiscard()) setDraft(report.content);
                }}
              >
                {copy.reset}
              </Button>
              <Button
                disabled={saving}
                onClick={(): void => {
                  if (canDiscard()) void dispatch(loadQualityText(report.id));
                }}
              >
                {copy.reload}
              </Button>
              <Button
                variant="contained"
                disabled={saving || !dirty || !draft.trim()}
                onClick={(): void => {
                  void dispatch(
                    saveQualityText({
                      id: report.id,
                      data: {
                        content: draft,
                        expectedUpdatedAt: report.updatedAt,
                      },
                    }),
                  );
                }}
              >
                {saving ? copy.saving : copy.save}
              </Button>
            </Stack>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}
